import { FC, memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { useFontStyle } from "~/hook/useFontStyle";
import useLocale from "~/hook/useLocale";
import { MediaItem, NodeDataBase, VideoCategoryNodeData } from "~/interface/video-category/type";
import { cn } from "~/lib/utils";
import RenderCircleItem from "../video-grid/RenderCircle";
import SquareCard from "../video-grid/SquareCard";
import VerticalCard from "../video-grid/VerticalCard";
import { DISPLAY_STYLE, DISPLAY_TYPE } from "../video-grid/video-data";
import VideoCard from "./VideoCard";
import VideoCardSkeleton from "./VideoCardSkeleton";

interface RelatedPlaylistProps {
  relatedPlaylistData: VideoCategoryNodeData;
  isLoading?: boolean;
  displayStyle?: string;
  isFourGrid?: boolean;
}

export const RELATED_PLAYLIST_QUERY = "related";

const RelatedPlaylist: FC<RelatedPlaylistProps> = ({ relatedPlaylistData, isLoading = false, displayStyle, isFourGrid }) => {
  const { video_category, slug, playlist_slug, last_slug } = useParams();
  const { semiBold } = useFontStyle();
  const { LocalizedLink } = useLocale();
  const { t } = useTranslation();

  const relatedData = useMemo(() => relatedPlaylistData, [relatedPlaylistData]);
  if (isLoading) {
    return (
      <div className="px-3 md:px-5 lg:px-0 py-2">
        <div className={cn("h-6 bg-gray-200 rounded mb-4 animate-pulse w-48", semiBold)}></div>
        <div className={cn("w-full grid grid-cols-2 gap-5 ", isFourGrid ? "md:grid-cols-4 lg:grid-cols-4" : "md:grid-cols-6 lg:grid-cols-6")}>
          {Array.from({ length: 8 }).map((_, index) => (
            <VideoCardSkeleton key={`skeleton-${index}`} type="square" />
          ))}
        </div>
      </div>
    );
  }

  // Show nothing if no data
  if (!relatedData || relatedData.length === 0) {
    return null;
  }

  const ViewCardOfRelated = (style: string, data: NodeDataBase) => {
    switch (style) {
      case DISPLAY_STYLE.rectangle:
        return (
          <VideoCard
            data={{
              video_id: data.video_id ?? null,
              name: data.name,
              node_type: data.node_type,
              part: data.count ?? undefined,
              img: data.img ?? null,
              record_date: (data as unknown as MediaItem).record_date || "",
              duration_sec: (data as unknown as MediaItem).duration_sec || 0,
              tag: (data as unknown as MediaItem).tag || null,
              square_img: null,
            }}
          />
        );
      case DISPLAY_STYLE.square:
        return (
          <SquareCard
            isChildrenCard={true}
            data={{
              video_id: data.video_id ?? null,
              name: data.name,
              node_type: data.node_type,
              part: data.count,
              img: data.img || null,
              tag: (data as unknown as MediaItem).tag || null,
              vertical_img: data?.vertical_img ?? null,
              square_img: data?.square_img ?? null,
              media_type: data?.media_type ?? null,
            }}
          />
        );
      case DISPLAY_STYLE.vertical:
        return (
          <VerticalCard
            isChildrenCard={true}
            data={{
              video_id: data.video_id || null,
              name: data.name,
              node_type: data.node_type,
              part: data.count || null,
              img: data.img || null,
              tag: (data as unknown as MediaItem).tag || null,
              vertical_img: data?.vertical_img || null,
              square_img: data?.square_img || null,
              media_type: data?.media_type || null,
            }}
          />
        );
      case DISPLAY_STYLE.circle:
        return (
          <RenderCircleItem
            item={{
              ...data,
              videoID: data.video_id ? data.video_id : "",
              media_type: data.media_type ? data.media_type : null,
            }}
            url_slug={slug || null}
            playlist_slug={playlist_slug || null}
            isBranch={data.node_type === DISPLAY_TYPE.branch || data.node_type === DISPLAY_TYPE.playlist_series}
            isRelated={true}
          />
        );
      default:
        return (
          <VideoCard
            data={{
              video_id: data.video_id ?? null,
              name: data.name,
              node_type: data.node_type,
              part: data.count ?? undefined,
              img: data.img ?? null,
              record_date: (data as unknown as MediaItem).record_date || "",
              duration_sec: (data as unknown as MediaItem).duration_sec || 0,
              tag: (data as unknown as MediaItem).tag || null,
              square_img: null,
            }}
          />
        );
    }
  };

  return (
    <div className="px-3 md:px-5 lg:px-0">
      {/* <h2 className={cn("body_text_l line-clamp-3 text-textHeadingColor mb-4", semiBold)}>{t("video.related_playlists")}</h2> */}
      <div className={cn("w-full grid grid-cols-2 gap-5 ", isFourGrid ? "md:grid-cols-3 lg:grid-cols-3" : "md:grid-cols-5 lg:grid-cols-6")}>
        {relatedData.map((item) => {
          const query = !item.node_type ? { watch: item.id } : undefined;

          return (
            <LocalizedLink key={item.id} to={item?.navigate_to} query={query} className="rounded-lg w-full">
              <VideoCard
                data={{
                  video_id: item.video_id ?? null,
                  name: item.name,
                  node_type: item.node_type,
                  part: item.count ?? undefined,
                  img: item.img ?? null,
                  record_date: (item as unknown as MediaItem).record_date || "",
                  duration_sec: (item as unknown as MediaItem).duration_sec || 0,
                  tag: (item as unknown as MediaItem).tag || null,
                  square_img: null,
                }}
              />
            </LocalizedLink>
          );
        })}
      </div>
    </div>
  );
};

export default memo(RelatedPlaylist);
